import { resourceType } from 'constants/loading';
import { findResource } from 'loading/finders';
import { isAppMode } from 'settings/selectors';
import { canProxy, isFieldsFromOlEnabled } from 'user/selectors';

import { fetchOwnedOperation, fetchSharedOperations, fetchAllOperations } from 'operations/loaders';
import { processAllOperations } from 'operations/processors';
import { parseAllOperations } from 'operations/evaParser';

import { addOperationIdsToFields } from 'fields/utils';
import { findAllFields, findFieldsByOperations } from 'fields/finders';
import { setAllFields } from 'fields';

import { findAllFarms } from 'farms/finders';

import { getAllOperations, getAllOperationUuids } from 'operations/selectors';
import { getActiveUserId } from 'current/selectors';
import { farmerNavigationStorageKeys, farmerNavigationDefaultOperation } from 'constants/operations';

export const findAllOperations = ({ dispatch, getState, forceReload=false, forceWait=false, onError }) => {
  return Promise.resolve()
  .then( () => {
    const state = getState();
    const userId = getActiveUserId(state);
    let fetchOperations = fetchAllOperations;
    // if app mode, the user will not be proxying, so they can always request anything accessible to them
    if (isAppMode(state)) {
      fetchOperations = fetchSharedOperations;
    } else {
      fetchOperations = canProxy(state) ? fetchOwnedOperation : fetchSharedOperations;
    }
    const resourceSpec = {
      type: resourceType.OPERATIONS,
      selector: () => getAllOperations(getState()),
      fetcher: () => fetchOperations({ userId }),
      processor: ( raw ) => processAllOperations({ dispatch, getState, operations: parseAllOperations( raw ) })
    };
    return findResource({ dispatch, getState, forceReload, forceWait, onError, ...resourceSpec });
  });
};

/**
 * Get default operation UUID
 * @param {Object} operations
 */
export const getDefaultOperationUuid = (operations = {}) => {

  if (!operations) {
    return farmerNavigationDefaultOperation;
  }

  const ops = Object.keys(operations).map(x => ({ 
    ...operations[x],
    operationUUID: x
  }));
  const owned = ops.filter(op => op.permission === 'OWNER');

  if (ops.length === 1 || (owned.length === 0 && ops.length > 0)) {
    return ops[0].operationUUID;
  } else if (owned.length > 0) {
    return owned[0].operationUUID;
  } else {
    return farmerNavigationDefaultOperation;
  }
};

export const findAllOpsAndFields = async (
  { dispatch, getState, forceReload=false, selectedOperation = undefined, onError }
) => {
  if (isFieldsFromOlEnabled(getState())) {
    const operations = await findAllOperations({ dispatch, getState, forceWait: true });
    const allOperationsUuids = getAllOperationUuids(getState());
    const storedOperation = sessionStorage.getItem(farmerNavigationStorageKeys.OPERATION_SELECTED);
    const defaultOperationUuid = selectedOperation 
      || (allOperationsUuids.includes(storedOperation) && storedOperation)
      || getDefaultOperationUuid(operations);
    const operationsIds = defaultOperationUuid === farmerNavigationDefaultOperation
      ? allOperationsUuids
      : [defaultOperationUuid];
    const fields = await findFieldsByOperations({ dispatch, getState, operationsIds });
    
    return { defaultOperationUuid, fields };
  }

  return Promise.all([
    findAllOperations({ dispatch, getState, forceReload, forceWait: true, onError }),
    findAllFields({ dispatch, getState, forceReload, forceWait: true, onError }),
    findAllFarms({ dispatch, getState, forceReload, forceWait: true, onError })
  ])
  .then( ([ operations, fields ]) => {
    const updatedFields = addOperationIdsToFields({ operations, fields });
    dispatch( setAllFields(updatedFields) );
    const defaultOperationUuid = getDefaultOperationUuid(operations);
    return { fields: updatedFields, defaultOperationUuid };
  });
};
